import { ref, type Ref } from 'vue'
import type { OrderCardDTO } from '@/api/orders'
import { fetchCareOrderListAllPages } from '@/utils/careOrderListCache'
import { applyTechnicianRefsToOrderCard } from '@/utils/technicianNoFromList'
import type { TechnicianNoLookup } from '@/utils/technicianNoFromList'
import type { CareScheduleFocusPayload, OrderScheduleAssignment, UnscheduledItem } from '../types'
import { parseServiceCycleDate, resolveServiceCycleDateFromOrderCard, ymdFromDate } from '../utils/scheduleCalendar'

export interface UnscheduledPoolDeps {
  findOrder: (orderId: string) => OrderCardDTO | undefined
  registerOrderCards: (orders: OrderCardDTO[]) => void
  careOrderIdsReferToSameOrder: (a: string, b: string) => boolean
  durationLabelForOrderId: (orderId: string) => string
  durationLabelForTemplateId: (templateId: number | undefined) => string
  lineIndexForScheduleCard: (o: OrderCardDTO, focusOrderId: string, focusLineIndex: number) => number
  isFollowUpScheduleFocusSession: (focus: CareScheduleFocusPayload) => boolean
  isRescheduleFollowUpFocusSession: (focus: CareScheduleFocusPayload) => boolean
  isOrderEligibleForReschedule: (o: OrderCardDTO) => boolean
  orderMatchesRescheduleSession: (o: OrderCardDTO, focus: CareScheduleFocusPayload) => boolean
  isOrderInUnscheduledPool: (o: OrderCardDTO, focus: CareScheduleFocusPayload) => boolean
  schedulePoolLineIndex: (o: OrderCardDTO, focus: CareScheduleFocusPayload) => number
  orderCardBelongsToFuPendingSchedulePool: (o: OrderCardDTO) => boolean
  orderFuScheduleLineAwaitingAssignment: (o: OrderCardDTO, focus: CareScheduleFocusPayload) => boolean
  schedulePoolOrderIsMainPendingSchedule: (o: OrderCardDTO) => boolean
  followUpScheduleLineIndex: (o: OrderCardDTO) => number
  lineIndexForRescheduleCard: (o: OrderCardDTO) => number
  technicianNoLookup: Ref<TechnicianNoLookup | null>
  assignmentsByOrderId: Ref<Record<string, OrderScheduleAssignment>>
  scheduleClearedByUser: Ref<Set<string>>
  isOrderScheduleCleared: (orderId: string) => boolean
  selectedOrderId: Ref<string>
  clearInspectorSidebarSelection: () => void
  selectUnscheduled: (item: UnscheduledItem) => void
  reloadScheduleContext: (focus: CareScheduleFocusPayload) => Promise<void>
}

export function useCareScheduleUnscheduled(
  focusPayload: Ref<CareScheduleFocusPayload | null>,
  poolDeps: UnscheduledPoolDeps,
  sharedUnscheduled?: Ref<UnscheduledItem[]>,
) {
  const unscheduled = sharedUnscheduled ?? ref<UnscheduledItem[]>([])

  function lineAddressForCard(o: OrderCardDTO, lineIndex: number): string {
    const line = o.services[lineIndex] ?? o.services[0]
    const primary = (line?.address ?? '').trim()
    if (primary && primary !== '—') return primary
    for (const svc of o.services) {
      const a = (svc.address ?? '').trim()
      if (a && a !== '—') return a
    }
    return ''
  }

  function cardFromOrder(
    o: OrderCardDTO,
    lineIndex: number,
    templateId?: number,
    durationOverride?: string,
  ): UnscheduledItem | null {
    const line = o.services[lineIndex] ?? o.services[0]
    if (!line) return null
    const addr = lineAddressForCard(o, lineIndex)
    const shortAddr = addr.split(',')[0]?.trim() || addr || o.homeId
    const durationLabel =
      (durationOverride ?? '').trim() || poolDeps.durationLabelForTemplateId(templateId)
    const cycle = resolveServiceCycleDateFromOrderCard(o, lineIndex)
    const serviceCycleYmd = cycle
      ? ymdFromDate(cycle)
      : o.serviceDate?.trim().match(/(\d{4}-\d{2}-\d{2})/)?.[1]
    return {
      orderId: o.id,
      address: shortAddr,
      serviceName: (line.serviceName ?? '').trim(),
      fullAddress: addr || o.homeId,
      durationLabel,
      serviceCycleYmd,
    }
  }

  /** Reschedule 弹框：仅 Unassign 后左栏展示 focus 单；打开时仍已排期则不显示 */
  function shouldShowReschedulePoolFocusOrder(focus: CareScheduleFocusPayload): boolean {
    const focusId = focus.orderId?.trim()
    if (!focusId || focus.mode !== 'reschedule') return false
    return poolDeps.isOrderScheduleCleared(focusId)
  }

  function resolveFocusOrderCard(
    focus: CareScheduleFocusPayload,
    candidates: OrderCardDTO[],
  ): OrderCardDTO | undefined {
    const focusId = focus.orderId?.trim()
    if (!focusId) return undefined
    const exact =
      candidates.find((o) => (o.id ?? '').trim() === focusId) ??
      focus.orderList?.find((o) => (o.id ?? '').trim() === focusId)
    if (exact) return exact
    for (const o of candidates) {
      if (poolDeps.careOrderIdsReferToSameOrder(o.id, focusId)) return o
    }
    const fromList = focus.orderList?.find((o) =>
      poolDeps.careOrderIdsReferToSameOrder(o.id, focusId),
    )
    if (fromList) return fromList
    return poolDeps.findOrder(focusId)
  }

  function unscheduledItemsForFocusOrderOnly(
    orders: OrderCardDTO[],
    focus: CareScheduleFocusPayload,
  ): UnscheduledItem[] {
    const focusOrder = resolveFocusOrderCard(focus, orders)
    if (focusOrder) {
      poolDeps.registerOrderCards([focusOrder])
      if (focus.mode === 'reschedule') {
        if (!shouldShowReschedulePoolFocusOrder(focus)) return []
        const card = cardFromOrder(focusOrder, poolDeps.lineIndexForRescheduleCard(focusOrder))
        if (card) return [card]
      } else {
        const items = unscheduledItemsFromScheduleOrders([focusOrder], focus)
        if (items.length) return items
      }
    }
    return [buildFocusUnscheduledCard(focus)]
  }

  function buildFocusUnscheduledCard(focus: CareScheduleFocusPayload): UnscheduledItem {
    const focusLine =
      focus.orderList?.find((o) => o.id === focus.orderId)?.services[focus.lineIndex] ??
      focus.orderList?.find((o) => o.id === focus.orderId)?.services[0]
    const focusFullAddress = (focusLine?.address ?? focus.address).trim()
    const fromList = focus.orderList?.find((o) => o.id === focus.orderId)
    if (fromList) {
      const idx = poolDeps.lineIndexForScheduleCard(fromList, focus.orderId, focus.lineIndex)
      const card = cardFromOrder(fromList, idx, undefined, focus.durationLabel)
      if (card) return card
    }
    return (
      cardFromOrder(
        {
          id: focus.orderId,
          homeId: focus.homeId ?? '',
          status: '',
          serviceDate: focus.serviceCycleDate,
          placementDate: '',
          services: [
            {
              serviceName: focus.serviceName,
              productCode: '',
              scheduledAt: focus.serviceCycleDate ?? null,
              address: focusFullAddress,
              assignment: '',
              price: '',
              paymentMethod: '',
              canSchedule: true,
              canReschedule: false,
              canRefund: false,
              canMessage: false,
              canNote: false,
            },
          ],
        },
        0,
        undefined,
        focus.durationLabel,
      ) ?? {
        orderId: focus.orderId,
        address: focus.address.split(',')[0]?.trim() || focus.address,
        serviceName: focus.serviceName.trim(),
        fullAddress: focusFullAddress || focus.address,
        durationLabel: (focus.durationLabel ?? '').trim(),
        serviceCycleYmd: focus.serviceCycleDate?.trim().match(/(\d{4}-\d{2}-\d{2})/)?.[1],
      }
    )
  }

  function finalizeUnscheduledItems(
    items: UnscheduledItem[],
    focus: CareScheduleFocusPayload,
  ): UnscheduledItem[] {
    if (!focus.orderId?.trim()) return items
    if (focus.mode === 'reschedule' && !shouldShowReschedulePoolFocusOrder(focus)) {
      return items.filter(
        (x) => !poolDeps.careOrderIdsReferToSameOrder(x.orderId, focus.orderId!),
      )
    }
    if (items.some((x) => poolDeps.careOrderIdsReferToSameOrder(x.orderId, focus.orderId))) return items
    const focusOrder = poolDeps.findOrder(focus.orderId)
    const focusMainPending = focusOrder
      ? poolDeps.schedulePoolOrderIsMainPendingSchedule(focusOrder)
      : focus.mode !== 'reschedule'
    if (
      focus.mode !== 'reschedule' &&
      poolDeps.assignmentsByOrderId.value[focus.orderId] &&
      !poolDeps.isOrderScheduleCleared(focus.orderId) &&
      !focusMainPending
    ) {
      return items
    }
    if (focus.mode === 'reschedule') {
      if (!focusOrder || !poolDeps.orderMatchesRescheduleSession(focusOrder, focus)) return items
    } else if (poolDeps.isFollowUpScheduleFocusSession(focus)) {
      /* FU Pending Schedule：从某单进入时左侧始终展示该入口单 */
    } else {
      const inPool = focusOrder ? poolDeps.isOrderInUnscheduledPool(focusOrder, focus) : false
      const fuAwaiting =
        focusOrder &&
        poolDeps.orderFuScheduleLineAwaitingAssignment(focusOrder, focus) &&
        !(
          poolDeps.assignmentsByOrderId.value[focus.orderId] &&
          !poolDeps.isOrderScheduleCleared(focus.orderId)
        )
      if (!inPool && !fuAwaiting && !focusMainPending) return items
    }
    const focusCard = buildFocusUnscheduledCard(focus)
    return [focusCard, ...items]
  }

  function buildUnscheduledItemsFromOrderList(focus: CareScheduleFocusPayload): UnscheduledItem[] {
    poolDeps.registerOrderCards(focus.orderList ?? [])
    return unscheduledItemsForFocusOrderOnly(focus.orderList ?? [], focus)
  }

  function ensureFocusOrderSelected(
    focus: CareScheduleFocusPayload | null,
    opts?: { forceFocusOrder?: boolean },
  ) {
    if (!focus) return
    const orderId = focus.orderId?.trim()
    if (!orderId) return

    const selected = poolDeps.selectedOrderId.value?.trim()
    if (!opts?.forceFocusOrder && selected) {
      const selectedStillInPool = unscheduled.value.some((x) =>
        poolDeps.careOrderIdsReferToSameOrder(x.orderId, selected),
      )
      if (selectedStillInPool && !poolDeps.careOrderIdsReferToSameOrder(selected, orderId)) {
        return
      }
    }

    // Reschedule 且仍已排期：左栏为空，选中态在 Inspectors，勿清掉侧栏高亮
    const rescheduleBooked =
      focus.mode === 'reschedule' && !poolDeps.isOrderScheduleCleared(orderId)
    if (!rescheduleBooked) {
      poolDeps.clearInspectorSidebarSelection()
    }
    if (!unscheduled.value.some((x) => poolDeps.careOrderIdsReferToSameOrder(x.orderId, orderId))) {
      unscheduled.value = finalizeUnscheduledItems(unscheduled.value, focus)
    }
    const item = unscheduled.value.find((x) => poolDeps.careOrderIdsReferToSameOrder(x.orderId, orderId))
    if (item) {
      if (!poolDeps.careOrderIdsReferToSameOrder(poolDeps.selectedOrderId.value, orderId)) {
        poolDeps.selectUnscheduled(item)
        poolDeps.selectedOrderId.value = orderId
      } else {
        poolDeps.selectedOrderId.value = orderId
      }
      return
    }
    poolDeps.selectedOrderId.value = orderId
  }

  async function fetchReschedulePoolFromApi(focus: CareScheduleFocusPayload): Promise<OrderCardDTO[]> {
    const fuRescheduleSession = poolDeps.isRescheduleFollowUpFocusSession(focus)
    const rescheduleStatuses = fuRescheduleSession
      ? (['fu_pending_reschedule', 'fu_assigned'] as const)
      : (['pending_reschedule', 'assigned'] as const)
    const pageResults = await Promise.all(
      rescheduleStatuses.map((serviceStatus) =>
        fetchCareOrderListAllPages(
          {
            pageSize: 100,
            searchField: 'order_id',
            serviceStatus,
          },
          { bypassCache: true },
        ),
      ),
    )
    const merged: OrderCardDTO[] = []
    const seen = new Set<string>()
    for (const { list } of pageResults) {
      for (const o of list) {
        if (seen.has(o.id)) continue
        if (!poolDeps.isOrderEligibleForReschedule(o)) continue
        if (!poolDeps.orderMatchesRescheduleSession(o, focus)) continue
        seen.add(o.id)
        merged.push(o)
      }
    }
    return merged
  }

  function unscheduledItemsFromScheduleOrders(
    orders: OrderCardDTO[],
    focus: CareScheduleFocusPayload,
  ): UnscheduledItem[] {
    const followUpSession = poolDeps.isFollowUpScheduleFocusSession(focus)
    return orders
      .map((o) => {
        const idx = followUpSession
          ? poolDeps.schedulePoolLineIndex(o, focus)
          : o.services.findIndex((l) => l.canSchedule) >= 0
            ? o.services.findIndex((l) => l.canSchedule)
            : 0
        const durationOverride = o.id === focus.orderId ? focus.durationLabel : undefined
        return cardFromOrder(o, idx, undefined, durationOverride)
      })
      .filter((x): x is UnscheduledItem => x != null)
  }


  function contextUnscheduledCards(contextOrders: OrderCardDTO[]): OrderCardDTO[] {
    const lookup = poolDeps.technicianNoLookup.value
    return contextOrders
      .map((o) => (lookup ? applyTechnicianRefsToOrderCard(o, lookup) : o))
      .filter((o): o is OrderCardDTO => !!o?.id?.trim())
  }

  function mergeContextOrdersWithFocusPool(
    focus: CareScheduleFocusPayload,
    contextOrders: OrderCardDTO[],
  ): OrderCardDTO[] {
    const fromContext = contextUnscheduledCards(contextOrders)
    poolDeps.registerOrderCards(fromContext)

    const byId = new Map<string, OrderCardDTO>()
    for (const o of fromContext) {
      byId.set(o.id.trim(), o)
    }
    for (const o of focus.orderList ?? []) {
      const id = o.id?.trim()
      if (!id || byId.has(id)) continue
      byId.set(id, poolDeps.findOrder(id) ?? o)
    }
    return [...byId.values()]
  }

  function applyScheduleUnscheduledFromContextOrders(
    focus: CareScheduleFocusPayload,
    orders: OrderCardDTO[],
  ): void {
    const fromContext = contextUnscheduledCards(orders)
    const merged = mergeContextOrdersWithFocusPool(focus, orders)
    const focusId = focus.orderId?.trim()
    const localFocus =
      (focusId ? poolDeps.findOrder(focusId) : undefined) ||
      focus.orderList?.find((o) => focusId && poolDeps.careOrderIdsReferToSameOrder(o.id, focusId))
    const preferLocalFocus =
      !!localFocus && !!focusId && poolDeps.isOrderScheduleCleared(focusId)
    const focusOrder = preferLocalFocus
      ? localFocus
      : resolveFocusOrderCard(focus, [...fromContext, ...merged])
    if (focusOrder) {
      focus.orderList = [focusOrder]
    }

    unscheduled.value = finalizeUnscheduledItems(
      unscheduledItemsForFocusOrderOnly([...fromContext, ...merged], focus),
      focus,
    )
    ensureFocusOrderSelected(focus)
  }

  function rebuildUnscheduledPoolFromFocus(focus: CareScheduleFocusPayload | null) {
    if (!focus) return
    unscheduled.value = finalizeUnscheduledItems(
      buildUnscheduledItemsFromOrderList(focus),
      focus,
    )
    ensureFocusOrderSelected(focus)
  }

  async function loadUnscheduledList(focus: CareScheduleFocusPayload) {
    if (focus.mode === 'reschedule') {
      try {
        const merged = await fetchReschedulePoolFromApi(focus)
        poolDeps.registerOrderCards(merged)
        unscheduled.value = finalizeUnscheduledItems(
          unscheduledItemsForFocusOrderOnly(merged, focus),
          focus,
        )
        ensureFocusOrderSelected(focus)
      } catch {
        unscheduled.value = finalizeUnscheduledItems([], focus)
        ensureFocusOrderSelected(focus)
      }
      return
    }

    await poolDeps.reloadScheduleContext(focus)
  }

  function refreshUnscheduledFromFocusOrderList(): Promise<void> {
    const focus = focusPayload.value
    if (!focus) return Promise.resolve()
    return loadUnscheduledList(focus)
  }

  return {
    unscheduled,
    cardFromOrder,
    buildUnscheduledItemsFromOrderList,
    finalizeUnscheduledItems,
    buildFocusUnscheduledCard,
    ensureFocusOrderSelected,
    applyScheduleUnscheduledFromContextOrders,
    rebuildUnscheduledPoolFromFocus,
    loadUnscheduledList,
    refreshUnscheduledFromFocusOrderList,
  }
}